import { Fab } from '@/components/elements/forms/buttons';
import Rating from '@/components/elements/redesign/Rating/Rating';
import Icon from '@/components/icons/Icon';
import { ReadMore } from '@/components/modules/readmore';
import { shouldDisplayReviews } from '@/helpers';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { Place } from '@/types/state/shared';

const baseTranslationKey = 'features.PlacePages.modules.Header';

const Header = ({
  place,
  onReviewsClick,
  onFavoriteClick,
}: {
  place: Place;
  onReviewsClick?: () => void;
  onFavoriteClick?: () => void;
}) => {
  const { isMobileView } = useMobileView();
  const { about = {}, contact = {}, reviews = {} } = place;
  const address = contact.address ? contact.address : null;
  const { stats = {} } = reviews || {};
  const { count, score } = stats;
  const user = useAppSelector((state) => state.users.user);
  const isFavorite = user ? user.favorites.indexOf(place.id) !== -1 : false;

  return (
    <div className={`gap-xl flex flex-col ${isMobileView ? '' : 'justify-between'}`}>
      <div className={`flex flex-col ${isMobileView ? 'gap-xs' : 'gap-md'}`}>
        <div className="flex justify-between">
          <h1 className={`text-fg_primary ${isMobileView ? 'text-h-l' : 'text-h-xl'} font-bold`}>{about.name}</h1>
          {isMobileView && (
            <Fab
              size="md"
              onClick={onFavoriteClick}
              aria-label={_s(`${baseTranslationKey}.favorites.${isFavorite ? 'remove' : 'add'}`)}
              variant="secondary"
              icon={<Icon variant={`heart${isFavorite ? '-filled' : ''}`} color="fg_primary" />}></Fab>
          )}
        </div>
        <div className={`gap-xs flex items-center`}>
          {(address?.zipcode || address?.city) && (
            <div className="text-fg_secondary text-m pr-2xs flex">
              {address?.street && <span>{address.street.trim() + ','}&nbsp;</span>}
              {address?.zipcode && <span>{address.zipcode.trim()}</span>}
              {address?.city && <span>&nbsp;{address.city.trim()}</span>}
            </div>
          )}
        </div>
      </div>

      <div className={`flex w-full ${isMobileView ? 'gap-xl flex-col' : ''}`}>
        <div className="flex-[2_1_0%]">
          <ReadMore maxLines={2} text={about.welcomeText} className="text-fg_primary text-m" />
        </div>
        {shouldDisplayReviews(place) && count > 4 && (
          <div className={`flex flex-[1_1_0%] items-start ${isMobileView ? '' : 'justify-end'}`}>
            <Rating
              count={count ?? 0}
              rating={score ?? 0}
              size={isMobileView ? 'md' : 'lg'}
              showCount={true}
              link={true}
              onReviewsClick={onReviewsClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
